import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import client from "../../api/client.js";
import { fetchUptimeBuckets } from "../../api/monitors.js";
import { fmtMs } from "../../utils/format.js";

const BUCKET_OPTIONS = [
  { value: "5m", label: "5 min" },
  { value: "1h", label: "1 hour" },
  { value: "6h", label: "6 hours" },
  { value: "1d", label: "1 day" },
];

export default function ResponseTimeChart({ monitorId, rangeHours, chartTitleKey = "response_chart.title" }) {
  const { t } = useTranslation("monitor_detail");
  const [bucketSize, setBucketSize] = useState("1h");
  const [buckets, setBuckets] = useState([]);
  const [baselineP50, setBaselineP50] = useState(null);

  useEffect(() => {
    if (!monitorId) return;
    async function loadBuckets() {
      const now = new Date();
      const from = new Date(now.getTime() - rangeHours * 3600 * 1000);
      try {
        const data = await fetchUptimeBuckets(monitorId, {
          bucket: bucketSize,
          from_time: from.toISOString(),
          to_time: now.toISOString(),
        });
        setBuckets(data ?? []);
      } catch {
        // silently ignore
      }
    }
    loadBuckets();
  }, [monitorId, rangeHours, bucketSize]);

  useEffect(() => {
    if (!monitorId) return;
    async function loadBaseline() {
      try {
        const res = await client.get(`/monitors/${monitorId}/baseline`);
        setBaselineP50(res.data.data?.p50_ms ?? null);
      } catch {
        // silently ignore
      }
    }
    loadBaseline();
  }, [monitorId]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">{t(chartTitleKey)}</h2>
        <div className="flex gap-1">
          {BUCKET_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setBucketSize(opt.value)}
              className={[
                "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                bucketSize === opt.value
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
              ].join(" ")}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {buckets.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">
          {t("response_chart.empty")}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart
            data={buckets}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="p50Grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0D9488" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#0D9488" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="p95Grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f97316" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis
              dataKey="bucket"
              tickFormatter={(t) =>
                new Date(t).toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11 }}
              width={48}
              tickFormatter={(v) => fmtMs(v)}
            />
            <Tooltip
              formatter={(v, name) => [fmtMs(v), name]}
              labelFormatter={(t) => new Date(t).toLocaleString()}
            />
            {baselineP50 != null && (
              <ReferenceLine
                y={baselineP50}
                stroke="#0D9488"
                strokeDasharray="4 2"
                label={{ value: t("response_chart.baseline_label"), position: "insideTopRight", fontSize: 10, fill: "#0D9488" }}
              />
            )}
            <Area
              type="monotone"
              dataKey="p95_ms"
              stroke="#f97316"
              strokeWidth={1}
              fill="url(#p95Grad)"
              dot={false}
              name="P95"
            />
            <Area
              type="monotone"
              dataKey="p50_ms"
              stroke="#0D9488"
              strokeWidth={2}
              fill="url(#p50Grad)"
              dot={false}
              name="P50"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
